import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Alert, Spinner, Image } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { storage } from '../../firebaseConfig';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import MainContainer from '../common/MainContainer';
import { Product } from '../../model/ProducModel';
import { FireStoreCategory } from '../../model/CategoryModel';
import { ProductGateway } from '../../gateways/ProductGateway';
import { CategoryGateway } from '../../gateways/CategoryGateway';
import { useGlobal } from '../../contexts/GlobalContext';
import ImageSelectorModal from '../common/ImageSelectorModal';
import { Utils } from '../../common/Utils';
import ImageCropModal from '../common/ImageCropModal';
import HelpIcon from '../common/HelpIcon';

const ManageProduct: React.FC = () => {
    const { productId } = useParams();
    const [product, setProduct] = useState<Product>({ name: '', price: 0, description: '', category: '', status: 'Active', attributes: [] });
    const [categories, setCategories] = useState<FireStoreCategory[]>([]);
    const [image, setImage] = useState<Blob | null>(null);
    const [priceWithoutVAT, setPriceWithoutVAT] = useState<string>("");
    const [saveText, setSaveText] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const { settings } = useGlobal();
    const [showCropModal, setShowCropModal] = useState(false);
    const [croppedImageBlob, setCroppedImageBlob] = useState<Blob | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [croppedImagePreviewUrl, setCroppedImagePreviewUrl] = useState<string | null>(null);
    const navigate = useNavigate();

    // Fetch categories from Firestore
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const categories = await CategoryGateway.GetCategories();
                setCategories(categories);
            } catch (err) {
                setError('Error fetching categories.');
            }
        };

        const fetchProduct = async () => {
            if (!productId) return;
            setLoading(true);
            try {
                const product = await ProductGateway.GetProduct(productId);
                if (product) {
                    setProduct(product);
                    if (settings.vatPercentage) {
                        setPriceWithoutVAT((product.price / (1 + settings.vatPercentage / 100)).toFixed(0));
                    }
                } else {
                    setError('Product not found.');
                }
            } catch (err) {
                setError('Error fetching product.');
            }
            setLoading(false);
        };

        fetchCategories();
        fetchProduct();
    }, [productId, settings.vatPercentage]);

    useEffect(() => {
        if (!image && fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }, [image]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setProduct({ ...product, [e.target.name]: e.target.value });
        if (e.target.name === "price") {
            setPriceWithoutVAT((Number(e.target.value) / (1 + settings.vatPercentage / 100)).toFixed(2));
        }
    };

    const handlePriceWithoutVATChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPriceWithoutVAT(e.target.value);
        setProduct({ ...product, price: Number((Number(e.target.value) * (1 + settings.vatPercentage / 100)).toFixed(0)) });
    };

    const handleAttributeChange = (index: number, key: string, value: string) => {
        const newAttributes = [...product.attributes!];
        newAttributes[index] = { ...newAttributes[index], [key]: value };
        setProduct({ ...product, attributes: newAttributes });
    };

    const addAttribute = () => {
        setProduct({ ...product, attributes: [...product.attributes!, { name: '', value: '', price: 0 }] });
    };

    const removeAttribute = (index: number) => {
        const newAttributes = product.attributes!.filter((_, i) => i !== index);
        setProduct({ ...product, attributes: newAttributes });
    };

    const uploadImageAndSaveProduct = async () => {

        setLoading(true);
        if (!croppedImageBlob) {
            return productId ? updateProduct(product.imageUrl) : addProduct(product.imageUrl);
        }

        const storageRef = ref(storage, `categories/${Date.now()}_${product.name}.jpg`);
        const uploadTask = uploadBytesResumable(storageRef, croppedImageBlob);

        uploadTask.on(
            'state_changed',
            null,
            (error) => {
                setLoading(false);
                setError('Error uploading image.');
                console.error("Error saving the image", error);
            },
            async () => {
                setLoading(true);
                const imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
                setProduct((prev) => ({ ...prev, imageUrl }));
                productId ? updateProduct(imageUrl) : addProduct(imageUrl);
            }
        );
    };

    const addProduct = async (imageUrl?: string) => {
        try {
            const id = await ProductGateway.AddProduct({ ...product, imageUrl: imageUrl ?? '' });
            setCroppedImagePreviewUrl(null);
            setCroppedImageBlob(null);
            setSaveText('Saved Successfully');
            navigate(Utils.GetUrl(`/admin/product/${id}`));
            // Wait 2 seconds before resetting
            setTimeout(() => {
                setSaveText('');
                setLoading(false);
            }, 2000);

        } catch (err: any) {
            setError('Error saving product.');
            console.error("Error saving:", err);
        }
    };

    const updateProduct = async (imageUrl?: string) => {
        try {
            await ProductGateway.UpdateProduct(productId!, { ...product, imageUrl: imageUrl ?? '' });
            setCroppedImagePreviewUrl(null);
            setCroppedImageBlob(null);
            setSaveText('Saved Successfully');
            // Wait 2 seconds before resetting
            setTimeout(() => {
                setSaveText('');
                setLoading(false);
            }, 2000);

        } catch (err: any) {
            setError('Error updating product.');
            console.error("Error updating:", err);
        }
    };

    const deleteImage = async () => {
        if (!product.imageUrl) return;
        try {
            setLoading(true);
            const imageRef = ref(storage, product.imageUrl);
            await deleteObject(imageRef);
            await ProductGateway.UpdateProduct(productId!, { ...product, imageUrl: '' })
            setProduct((prev) => ({ ...prev, imageUrl: '' }));
        } catch {
            setError('Error deleting image.');
        }
        finally {
            setLoading(false);
        }
    };

    return (
        <MainContainer>
            <Form onSubmit={(e) => { e.preventDefault(); uploadImageAndSaveProduct(); }} className="p-3">
                {loading && <Spinner animation="border" />}
                {error && <Alert variant="danger">{error}</Alert>}

                <h2 className="mb-4">{productId ? 'Update Product' : 'Add Product'}</h2>

                <Form.Group controlId="productName" className="mb-3">
                    <Form.Label>Product Name</Form.Label>
                    <Form.Control type="text" name="name" value={product.name} onChange={handleChange} required />
                </Form.Group>

                <Form.Group controlId="productDescription" className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control as="textarea" name="description" value={product.description} onChange={handleChange} />
                </Form.Group>
                {settings && settings.vatPercentage && settings.vatPercentage > 0 &&
                    <Form.Group controlId="productPrice" className="mb-3">
                        <Form.Label>Price without VAT</Form.Label>
                        <Form.Control type="number" name="priceWithoutVAT" value={priceWithoutVAT} onChange={handlePriceWithoutVATChange} required />
                    </Form.Group>
                }
                <Form.Group controlId="productPriceWithVAT" className="mb-3">
                    <Form.Label>{settings && settings.vatPercentage && settings.vatPercentage > 0 ? `Price with VAT` : "Price"}</Form.Label>
                    <Form.Control type="number" name="price" value={product.price} onChange={handleChange} required />
                </Form.Group>

                <Form.Group controlId="productCategory" className="mb-3">
                    <Form.Label>Category</Form.Label>
                    <Form.Select name="category" value={product.category} onChange={handleChange} required>
                        <option value="">Select a category</option>
                        {categories.map(firestoreCategory => (
                            <option key={firestoreCategory.id} value={firestoreCategory.id}>{firestoreCategory.category.name}</option>
                        ))}
                    </Form.Select>
                </Form.Group>

                <Form.Group controlId="productStatus" className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select name="status" value={product.status} onChange={handleChange} required>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                    </Form.Select>
                </Form.Group>

                <Form.Group controlId="productAttributes" className="mb-3">
                    <Form.Label>Attributes (e.g. Milk Type)&nbsp;
                        <HelpIcon
                            title="Product Attributes"
                            htmlContent={`
<p><strong>Attributes</strong> allow you to define specific characteristics of a product that customers can choose from, often with different pricing.</p>
    <p>For example, a Flat White coffee can be made with different types of milk.</p>
    <p>In this example, the attribute name must be 'Milk Type' and the attribute value can be 'Full Cream' and 'Almond Milk'.</p>
  `}
  
                            size={20}
                            placement="right"
                        />


                    </Form.Label>
                    {product.attributes?.map((attr, index) => (
                        <div key={index} className="d-flex gap-2">
                            <Form.Control type="text" placeholder="Attribute Name" value={attr.name} onChange={(e) => handleAttributeChange(index, 'name', e.target.value)} />
                            <Form.Control type="text" placeholder="Attribute Value" value={attr.value} onChange={(e) => handleAttributeChange(index, 'value', e.target.value)} />
                            <Form.Control type="number" placeholder="Attribute Price" value={attr.price} onChange={(e) => handleAttributeChange(index, 'price', e.target.value)} />
                            <Button variant="danger" onClick={() => removeAttribute(index)}>Delete</Button>
                        </div>
                    ))}
                    <div className="d-flex gap-2">
                        <Button variant="secondary" onClick={addAttribute} className="mt-2">Add Attribute</Button>
                    </div>
                </Form.Group>

                {!product.imageUrl && (<>
                    <Form.Group controlId="productImage" className="mb-3">
                        <Form.Label>Upload Image</Form.Label>
                        <Form.Control
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    setImage(file);
                                    setShowCropModal(true);
                                }
                            }}
                        />
                    </Form.Group>
                    <Form.Group controlId="productImageSearch" className="mb-3">
                        <Form.Label>Search Image</Form.Label>
                        <div className="d-flex gap-2">
                            <ImageSelectorModal
                                entityName={product.name}
                                onImageSelected={(blob) => {
                                    setImage(blob);
                                    setShowCropModal(true);
                                }}
                            />
                        </div>
                    </Form.Group></>
                )}

                {product.imageUrl && (
                    <div className='mb-3'>
                        <Image src={product.imageUrl} thumbnail width={150} />
                        <Button variant="danger" onClick={deleteImage} className="mt-2">{loading ? <Spinner animation="border" size="sm" /> : "Delete Image"}</Button>
                    </div>
                )}

                {croppedImagePreviewUrl && (
                    <div className='mb-3'>
                        <Image src={croppedImagePreviewUrl} thumbnail width={150} />
                        <div className="text-muted" style={{ fontStyle: "italic" }}>Cropped preview (not yet saved)</div>
                    </div>
                )}

                <Button variant="primary" type="submit">
                    {loading ? <Spinner animation="border" size="sm" /> : "Save Product"}</Button>
                <Button variant='secondary' type='button' className='mx-2' onClick={() => { navigate(Utils.GetUrl("/admin/products")) }}>
                    Back
                </Button>
                {saveText && <Alert variant="success" className="mb-0 mt-1 py-1 px-2">
                    {saveText}
                </Alert>}
            </Form>

            {showCropModal && image && (
                <ImageCropModal
                    show={showCropModal}
                    file={image}
                    onClose={() => {
                        setImage(null);
                        setShowCropModal(false);
                    }}
                    onUploadComplete={(blob) => {
                        setCroppedImageBlob(blob);
                        setImage(null);
                        const previewUrl = URL.createObjectURL(blob);
                        setCroppedImagePreviewUrl(previewUrl);
                        setShowCropModal(false);
                    }}
                />
            )}
        </MainContainer>
    );
};

export default ManageProduct;